const BASE = { stroke: "currentColor", fill: "none", vectorEffect: "non-scaling-stroke" };
const GRID = { ...BASE, strokeOpacity: 0.12, strokeWidth: 1 };
const SEC = { ...BASE, strokeOpacity: 0.45, strokeWidth: 1.2 };
const PRI = { ...BASE, strokeOpacity: 0.8, strokeWidth: 1.6 };
const TEXT = { fill: "currentColor", fontFamily: "Space Mono, monospace", fontSize: 8, letterSpacing: 0.6 };
const range = (count) => Array.from({ length: count }, (_, index) => index);
const seeded = (seed) => () => { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; };
const points = (list) => list.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");

const ServerHall = () => {
  const frame = (s) => ({ x: 200 - 200 * s, y: 110 - 110 * s, w: 400 * s, h: 240 * s });
  return (
    <g>
      {range(9).map((i) => { const f = frame(((i + 1) / 9) ** 1.8); return <rect key={i} x={f.x} y={f.y} width={f.w} height={f.h} {...(i % 3 === 2 ? SEC : GRID)} />; })}
      {range(9).map((i) => <line key={`f${i}`} x1={i * 50} y1={240} x2={200} y2={110} {...GRID} />)}
      {range(14).map((i) => { const s = ((i + 2) / 16) ** 1.6; return <g key={`r${i}`}><line x1={200 - 200 * s} x2={200 - 200 * s} y1={110 - 60 * s} y2={110 + 130 * s} {...SEC} /><line x1={200 + 200 * s} x2={200 + 200 * s} y1={110 - 60 * s} y2={110 + 130 * s} {...SEC} /></g>; })}
      {[[0, 0], [400, 0], [0, 240], [400, 240]].map(([x, y]) => <line key={`${x}${y}`} x1={x} y1={y} x2={200} y2={110} {...PRI} />)}
    </g>
  );
};

const NeuralGlobe = () => {
  const [cx, cy, r] = [210, 124, 92];
  const nodes = [[-40, -50], [30, -64], [62, -10], [10, 22], [-58, 18], [44, 52], [-20, 70]].map(([dx, dy]) => [cx + dx, cy + dy]);
  return (
    <g>
      {[0.26, 0.56, 0.82].map((k) => <ellipse key={k} cx={cx} cy={cy} rx={r * k} ry={r} {...SEC} />)}
      {[-60, -30, 0, 30, 60].map((lat) => { const rad = (lat * Math.PI) / 180; return <ellipse key={lat} cx={cx} cy={cy - r * Math.sin(rad)} rx={r * Math.cos(rad)} ry={9 * Math.cos(rad)} {...GRID} strokeOpacity={0.3} />; })}
      <circle cx={cx} cy={cy} r={r} {...PRI} />
      <polyline points={points([nodes[0], nodes[1], nodes[2], nodes[3], nodes[4], nodes[0], nodes[3], nodes[5], nodes[6], nodes[4]])} {...SEC} />
      {nodes.map(([x, y]) => <circle key={`${x}${y}`} cx={x} cy={y} r={2.6} fill="currentColor" fillOpacity={0.8} />)}
    </g>
  );
};

const SiliconDie = () => (
  <g>
    {range(9).map((i) => { const x = 140 + i * 15; return <g key={`v${i}`}><path d={`M${x} 70 V52 L${x + (i - 4) * 6} 36 V0`} {...(i % 2 ? GRID : SEC)} /><path d={`M${x} 170 V188 L${x + (i - 4) * 6} 204 V240`} {...(i % 2 ? GRID : SEC)} /></g>; })}
    {range(5).map((i) => { const y = 86 + i * 17; return <g key={`h${i}`}><path d={`M130 ${y} H104 L88 ${y + (i - 2) * 8} H0`} {...SEC} /><path d={`M270 ${y} H296 L312 ${y + (i - 2) * 8} H400`} {...SEC} /></g>; })}
    <rect x="130" y="70" width="140" height="100" {...PRI} />
    <rect x="146" y="84" width="108" height="72" {...SEC} />
    {range(4).map((col) => range(3).map((row) => <rect key={`${col}${row}`} x={156 + col * 24} y={92 + row * 21} width="16" height="13" {...GRID} strokeOpacity={0.35} />))}
  </g>
);

const LightFiber = () => (
  <g>
    {range(11).map((i) => {
      const y0 = 30 + i * 18, a = 40 + (i % 4) * 22;
      return <path key={i} d={`M-10 ${y0} C 120 ${y0 + a}, 260 ${y0 - a}, 410 ${y0 + (i % 3) * 14 - 14}`} {...(i % 3 === 0 ? PRI : i % 3 === 1 ? SEC : GRID)} />;
    })}
  </g>
);

const HexRain = () => {
  const next = seeded(7);
  return (
    <g>
      {range(15).map((col) => range(13).map((row) => {
        const value = next();
        if (value < 0.32) return null;
        return <text key={`${col}-${row}`} x={8 + col * 27} y={14 + row * 18} {...TEXT} fillOpacity={0.12 + next() * 0.5}>{Math.floor(value * 255).toString(16).toUpperCase().padStart(2, "0")}</text>;
      }))}
    </g>
  );
};

const contour = (cx, cy, base, step, level, phase) => points(range(73).map((i) => {
  const t = (i / 72) * Math.PI * 2;
  const radius = (base + level * step) * (1 + 0.14 * Math.sin(3 * t + level * 0.6 + phase) + 0.07 * Math.sin(5 * t - level));
  return [cx + radius * Math.cos(t) * 1.35, cy + radius * Math.sin(t)];
}));

const ThermalContours = () => (
  <g>
    {range(9).map((level) => <polyline key={level} points={contour(240, 132, 10, 15, level, 0)} {...(level % 3 === 0 ? PRI : level % 3 === 1 ? SEC : GRID)} />)}
    {range(4).map((level) => <polyline key={`b${level}`} points={contour(70, 56, 6, 11, level, 1.4)} {...(level === 0 ? SEC : GRID)} />)}
  </g>
);

const RadarSweep = () => {
  const [cx, cy] = [200, 128];
  const arc = (angle, radius) => [cx + radius * Math.cos(angle), cy + radius * Math.sin(angle)];
  const [a1, a2] = [-Math.PI / 2, -Math.PI / 6];
  const [x1, y1] = arc(a1, 112), [x2, y2] = arc(a2, 112);
  return (
    <g>
      {[28, 56, 84, 112].map((radius, i) => <circle key={radius} cx={cx} cy={cy} r={radius} {...(i === 3 ? PRI : SEC)} />)}
      <path d={`M${cx - 130} ${cy} H${cx + 130} M${cx} ${cy - 130} V${cy + 130}`} {...GRID} strokeOpacity={0.3} />
      <path d={`M${cx} ${cy} L${x1} ${y1} A112 112 0 0 1 ${x2} ${y2} Z`} fill="currentColor" fillOpacity={0.07} stroke="none" />
      <line x1={cx} y1={cy} x2={x2} y2={y2} {...PRI} />
      {[[40, -60], [72, -30], [-50, 40], [20, 70]].map(([dx, dy]) => <circle key={`${dx}${dy}`} cx={cx + dx} cy={cy + dy} r={2.6} fill="currentColor" fillOpacity={0.8} />)}
    </g>
  );
};

const BlueprintSpec = () => (
  <g>
    {range(21).map((i) => <line key={`gx${i}`} x1={i * 20} x2={i * 20} y1={0} y2={240} {...GRID} />)}
    {range(13).map((i) => <line key={`gy${i}`} x1={0} x2={400} y1={i * 20} y2={i * 20} {...GRID} />)}
    <rect x="120" y="70" width="160" height="110" {...PRI} />
    <circle cx="200" cy="125" r="30" {...SEC} />
    <path d="M200 60 V190 M110 125 H290" {...SEC} strokeDasharray="6 4" />
    <path d="M120 48 H280 M120 42 V54 M280 42 V54 M302 70 V180 M296 70 H308 M296 180 H308" {...SEC} />
    <text x="186" y="40" {...TEXT} fillOpacity={0.7}>W 160</text>
    <text x="312" y="128" {...TEXT} fillOpacity={0.7}>H 110</text>
    <text x="124" y="198" {...TEXT} fillOpacity={0.7}>SECTION A-A</text>
  </g>
);

const WaveformMesh = () => (
  <g>
    {range(14).map((row) => {
      const t = row / 13, base = 96 + t ** 1.6 * 140, amp = 5 + t * 20;
      return <polyline key={row} points={points(range(51).map((i) => [i * 8, base + amp * Math.sin(i * 0.22 + row * 0.55)]))} {...(row % 4 === 3 ? PRI : row % 2 ? SEC : GRID)} />;
    })}
  </g>
);

const CommandConsole = () => (
  <g>
    <path d="M24 44 V20 H48 M352 20 H376 V44 M376 196 V220 H352 M48 220 H24 V196" {...PRI} />
    <circle cx="200" cy="120" r="52" {...SEC} />
    <circle cx="200" cy="120" r="20" {...GRID} strokeOpacity={0.35} />
    <path d="M200 56 V76 M200 164 V184 M136 120 H156 M244 120 H264" {...PRI} />
    {[86, 64, 112, 48].map((width, i) => <line key={width} x1="40" x2={40 + width} y1={168 + i * 9} y2={168 + i * 9} {...(i === 0 ? PRI : SEC)} />)}
    <text x="40" y="40" {...TEXT} fillOpacity={0.7}>DCYB ACADEMY</text>
    <text x="296" y="210" {...TEXT} fillOpacity={0.7}>STATUS · LIVE</text>
  </g>
);

const DIRECTIONS = { serverHall: ServerHall, neuralGlobe: NeuralGlobe, siliconDie: SiliconDie, lightFiber: LightFiber, hexRain: HexRain, thermalContours: ThermalContours, radarSweep: RadarSweep, blueprintSpec: BlueprintSpec, waveformMesh: WaveformMesh, commandConsole: CommandConsole };

export const Artwork = ({ direction, className = "", testId }) => {
  const Direction = DIRECTIONS[direction];
  return (
    <svg data-testid={testId} viewBox="0 0 400 240" preserveAspectRatio="xMidYMid slice" aria-hidden="true" focusable="false" className={`block text-[#160907] ${className}`}>
      <Direction />
    </svg>
  );
};
